"use client";

import { useCallback, useState } from "react";

export type UseControlledOrUncontrolledParameters<
  TState,
  TArgs extends any[] = [],
> = {
  defaultValue: TState;
  value?: TState;
  onChange?: (value: TState, ...args: TArgs) => void;
};

// used for readonly controlled values
const noop = () => {};

export function useControlledOrUncontrolled<TState>(parameters: {
  defaultValue: TState;
  value?: TState;
  onChange?: React.Dispatch<React.SetStateAction<TState>>;
}): [TState, React.Dispatch<React.SetStateAction<TState>>];
export function useControlledOrUncontrolled<TState, TArgs extends any[] = []>(
  parameters: UseControlledOrUncontrolledParameters<TState, TArgs>
): [TState, (value: TState, ...args: TArgs) => void];
export function useControlledOrUncontrolled<TState, TArgs extends any[] = []>({
  defaultValue,
  value,
  onChange,
}: UseControlledOrUncontrolledParameters<TState, TArgs>) {
  const [internalValue, setInternalValue] = useState(value ?? defaultValue);

  const setValue = useCallback(
    (value: TState, ...args: TArgs) => {
      setInternalValue(value);
      onChange?.(value, ...args);
    },
    [onChange]
  );

  if (value !== undefined) {
    return [value, onChange ?? noop] as const;
  }

  return [internalValue, setValue] as const;
}
